import { useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  CreditCard,
  KeyRound,
  MapPin,
  MessageSquare,
  Pencil,
  Plug,
  Plus,
  Trash2,
  type LucideIcon,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CompactStatCard, FormInput, FormSelect, PageHeader, StatsGrid } from "@/components/shared";
import { useIntegrationSettings, useUpdateIntegrationSettingsMutation } from "@/hooks/queries";
import type {
  AuthSettingsDto,
  IntegrationSettingsDto,
  MapsIntegrationDto,
  RazorpayIntegrationDto,
  SmsIntegrationDto,
} from "@/types/integration.dto";
import { getApiErrorMessage } from "@/services/httpClient";
import { useToast } from "@/hooks/use-toast";

type ProviderKey = "razorpay" | "sms" | "auth" | "maps";

type ProviderMeta = {
  key: ProviderKey;
  title: string;
  description: string;
  icon: LucideIcon;
};

const PROVIDERS: ProviderMeta[] = [
  {
    key: "razorpay",
    title: "Razorpay",
    description: "Online payments",
    icon: CreditCard,
  },
  {
    key: "sms",
    title: "SMS",
    description: "OTP & notifications",
    icon: MessageSquare,
  },
  {
    key: "auth",
    title: "Auth & OTP",
    description: "Login & reset links",
    icon: KeyRound,
  },
  {
    key: "maps",
    title: "Maps",
    description: "Locator & distance",
    icon: MapPin,
  },
];

const defaultAuth = (): AuthSettingsDto => ({
  frontendBaseUrl: "http://localhost:8080",
  useDemoOtp: true,
  demoOtpCode: "123456",
  otpExpiryMinutes: 10,
  otpLength: 6,
});

const defaultRazorpay = (): RazorpayIntegrationDto => ({
  enabled: false,
  keyId: "",
  keySecret: "",
  webhookSecret: "",
  keySecretConfigured: false,
  webhookSecretConfigured: false,
});

const defaultSms = (): SmsIntegrationDto => ({
  enabled: false,
  provider: "twilio",
  accountSid: "",
  authToken: "",
  fromNumber: "",
  authTokenConfigured: false,
});

const defaultMaps = (): MapsIntegrationDto => ({
  enabled: false,
  provider: "google",
  apiKey: "",
  apiKeyConfigured: false,
});

function isProviderConfigured(settings: IntegrationSettingsDto, key: ProviderKey): boolean {
  switch (key) {
    case "razorpay":
      return Boolean(settings.razorpay.enabled || settings.razorpay.keyId || settings.razorpay.keySecretConfigured);
    case "sms":
      return Boolean(settings.sms.enabled || settings.sms.accountSid || settings.sms.authTokenConfigured);
    case "auth": {
      const auth = settings.auth;
      if (!auth) return false;
      const isDefault =
        auth.useDemoOtp === true &&
        (auth.demoOtpCode === "123456" || !auth.demoOtpCode) &&
        (auth.frontendBaseUrl === "http://localhost:8080" || !auth.frontendBaseUrl) &&
        (auth.otpExpiryMinutes ?? 10) === 10 &&
        (auth.otpLength ?? 6) === 6;
      return !isDefault;
    }
    case "maps":
      return Boolean(settings.maps.enabled || settings.maps.apiKeyConfigured);
    default:
      return false;
  }
}

function isProviderEnabled(settings: IntegrationSettingsDto, key: ProviderKey): boolean {
  switch (key) {
    case "razorpay":
      return settings.razorpay.enabled;
    case "sms":
      return settings.sms.enabled;
    case "auth":
      return !(settings.auth?.useDemoOtp ?? true);
    case "maps":
      return settings.maps.enabled;
    default:
      return false;
  }
}

function providerSummary(settings: IntegrationSettingsDto, key: ProviderKey): string {
  switch (key) {
    case "razorpay":
      return settings.razorpay.keyId || "No Key ID";
    case "sms":
      return `${settings.sms.provider || "twilio"}${settings.sms.fromNumber ? ` · ${settings.sms.fromNumber}` : ""}`;
    case "auth":
      return settings.auth?.useDemoOtp ? `Demo OTP · ${settings.auth.demoOtpCode}` : "Live SMS OTP";
    case "maps":
      return settings.maps.provider || "google";
    default:
      return "";
  }
}

const IntegrationsSection = () => {
  const { toast } = useToast();
  const { data, isLoading } = useIntegrationSettings();
  const updateMutation = useUpdateIntegrationSettingsMutation();
  const [settings, setSettings] = useState<IntegrationSettingsDto | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"create" | "edit">("create");
  const [activeKey, setActiveKey] = useState<ProviderKey | null>(null);
  const [draft, setDraft] = useState<IntegrationSettingsDto | null>(null);
  const [deleteKey, setDeleteKey] = useState<ProviderKey | null>(null);

  useEffect(() => {
    if (data) {
      setSettings({
        ...data,
        auth: data.auth ?? defaultAuth(),
      });
    }
  }, [data]);

  const configuredProviders = useMemo(() => {
    if (!settings) return [];
    return PROVIDERS.filter((p) => isProviderConfigured(settings, p.key));
  }, [settings]);

  const availableToAdd = useMemo(() => {
    if (!settings) return [];
    return PROVIDERS.filter((p) => !isProviderConfigured(settings, p.key));
  }, [settings]);

  if (isLoading || !settings) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-40 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  const openCreate = () => {
    if (availableToAdd.length === 0) {
      toast({ title: "All providers are already added", variant: "destructive" });
      return;
    }
    const first = availableToAdd[0].key;
    setDialogMode("create");
    setActiveKey(first);
    setDraft(structuredClone(settings));
    setDialogOpen(true);
  };

  const openEdit = (key: ProviderKey) => {
    setDialogMode("edit");
    setActiveKey(key);
    setDraft(structuredClone(settings));
    setDialogOpen(true);
  };

  const persist = async (next: IntegrationSettingsDto, successTitle: string) => {
    await updateMutation.mutateAsync({
      razorpay: next.razorpay,
      sms: next.sms,
      maps: next.maps,
      auth: next.auth,
    });
    setSettings(next);
    toast({ title: successTitle });
  };

  const handleDialogSave = async () => {
    if (!draft || !activeKey) return;
    try {
      // Ensure newly created providers are marked enabled when saved from create
      const next = structuredClone(draft);
      if (dialogMode === "create") {
        if (activeKey === "razorpay") next.razorpay.enabled = true;
        if (activeKey === "sms") next.sms.enabled = true;
        if (activeKey === "maps") next.maps.enabled = true;
      }
      await persist(next, dialogMode === "create" ? "Provider added" : "Provider updated");
      setDialogOpen(false);
      setActiveKey(null);
      setDraft(null);
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const handleDelete = async () => {
    if (!deleteKey || !settings) return;
    try {
      const next = structuredClone(settings);
      if (deleteKey === "razorpay") next.razorpay = defaultRazorpay();
      if (deleteKey === "sms") next.sms = defaultSms();
      if (deleteKey === "auth") next.auth = defaultAuth();
      if (deleteKey === "maps") next.maps = defaultMaps();
      await persist(next, "Provider removed");
      setDeleteKey(null);
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const meta = activeKey ? PROVIDERS.find((p) => p.key === activeKey) : null;

  const enabledCount = configuredProviders.filter((p) => isProviderEnabled(settings, p.key)).length;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Integrations"
        description="Add payment, SMS, auth, and maps providers. Secrets stay masked after save."
        actions={
          <Button size="sm" className="gap-1" onClick={openCreate} disabled={availableToAdd.length === 0}>
            <Plus className="h-4 w-4" /> Add Provider
          </Button>
        }
      />

      <StatsGrid columns={3}>
        <CompactStatCard title="Configured" value={configuredProviders.length} icon={Plug} />
        <CompactStatCard title="Enabled" value={enabledCount} icon={CheckCircle2} iconClassName="bg-gradient-primary" />
        <CompactStatCard title="Available to add" value={availableToAdd.length} icon={Plus} />
      </StatsGrid>

      {configuredProviders.length === 0 ? (
        <div className="glass-card flex flex-col items-center justify-center gap-3 rounded-xl border border-border py-10 text-center">
          <p className="text-sm text-muted-foreground">No providers configured yet.</p>
          <Button size="sm" className="gap-1" onClick={openCreate}>
            <Plus className="h-4 w-4" /> Add Provider
          </Button>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {configuredProviders.map((provider) => {
            const Icon = provider.icon;
            const enabled = isProviderEnabled(settings, provider.key);
            return (
              <div
                key={provider.key}
                className="glass-card flex items-center gap-3 rounded-xl border border-border p-3"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <p className="truncate text-sm font-semibold">{provider.title}</p>
                    <Badge variant={enabled ? "default" : "secondary"} className="px-1.5 py-0 text-[10px]">
                      {enabled ? "On" : "Off"}
                    </Badge>
                  </div>
                  <p className="truncate text-xs text-muted-foreground">{providerSummary(settings, provider.key)}</p>
                </div>
                <div className="flex shrink-0 gap-1">
                  <Button
                    size="icon"
                    variant="outline"
                    className="h-8 w-8"
                    title={`Edit ${provider.title}`}
                    onClick={() => openEdit(provider.key)}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="icon"
                    variant="outline"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    title={`Delete ${provider.title}`}
                    onClick={() => setDeleteKey(provider.key)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Sheet
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setActiveKey(null);
            setDraft(null);
          }
        }}
      >
        <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-y-auto p-0 sm:max-w-xl">
          <SheetHeader className="border-b px-6 py-4 text-left">
            <SheetTitle>
              {dialogMode === "create" ? "Add Provider" : `Edit ${meta?.title ?? "Provider"}`}
            </SheetTitle>
          </SheetHeader>

          {draft && activeKey && (
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">
              {dialogMode === "create" && (
                <FormSelect
                  label="Provider type"
                  value={activeKey}
                  onValueChange={(v) => setActiveKey(v as ProviderKey)}
                  options={availableToAdd.map((p) => ({ value: p.key, label: p.title }))}
                />
              )}

              {activeKey === "razorpay" && (
                <>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={draft.razorpay.enabled}
                      onCheckedChange={(v) =>
                        setDraft({ ...draft, razorpay: { ...draft.razorpay, enabled: v } })
                      }
                    />
                    <Label>Enabled</Label>
                  </div>
                  <FormInput
                    label="Key ID"
                    value={draft.razorpay.keyId ?? ""}
                    onChange={(e) =>
                      setDraft({ ...draft, razorpay: { ...draft.razorpay, keyId: e.target.value } })
                    }
                  />
                  <FormInput
                    label={draft.razorpay.keySecretConfigured ? "Key Secret (leave blank to keep)" : "Key Secret"}
                    type="password"
                    value={draft.razorpay.keySecret ?? ""}
                    onChange={(e) =>
                      setDraft({ ...draft, razorpay: { ...draft.razorpay, keySecret: e.target.value } })
                    }
                  />
                  <FormInput
                    label={
                      draft.razorpay.webhookSecretConfigured
                        ? "Webhook Secret (leave blank to keep)"
                        : "Webhook Secret"
                    }
                    type="password"
                    value={draft.razorpay.webhookSecret ?? ""}
                    onChange={(e) =>
                      setDraft({ ...draft, razorpay: { ...draft.razorpay, webhookSecret: e.target.value } })
                    }
                  />
                </>
              )}

              {activeKey === "sms" && (
                <>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={draft.sms.enabled}
                      onCheckedChange={(v) => setDraft({ ...draft, sms: { ...draft.sms, enabled: v } })}
                    />
                    <Label>Enabled</Label>
                  </div>
                  <FormInput
                    label="Provider"
                    value={draft.sms.provider}
                    onChange={(e) => setDraft({ ...draft, sms: { ...draft.sms, provider: e.target.value } })}
                  />
                  <FormInput
                    label="Account SID"
                    value={draft.sms.accountSid ?? ""}
                    onChange={(e) => setDraft({ ...draft, sms: { ...draft.sms, accountSid: e.target.value } })}
                  />
                  <FormInput
                    label={draft.sms.authTokenConfigured ? "Auth Token (leave blank to keep)" : "Auth Token"}
                    type="password"
                    value={draft.sms.authToken ?? ""}
                    onChange={(e) => setDraft({ ...draft, sms: { ...draft.sms, authToken: e.target.value } })}
                  />
                  <FormInput
                    label="From Number"
                    value={draft.sms.fromNumber ?? ""}
                    onChange={(e) => setDraft({ ...draft, sms: { ...draft.sms, fromNumber: e.target.value } })}
                  />
                </>
              )}

              {activeKey === "auth" && (
                <>
                  <FormInput
                    label="Frontend base URL"
                    value={draft.auth?.frontendBaseUrl ?? ""}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        auth: { ...(draft.auth ?? defaultAuth()), frontendBaseUrl: e.target.value },
                      })
                    }
                    hint="Used in password reset emails"
                  />
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={draft.auth?.useDemoOtp ?? true}
                      onCheckedChange={(v) =>
                        setDraft({
                          ...draft,
                          auth: { ...(draft.auth ?? defaultAuth()), useDemoOtp: v },
                        })
                      }
                    />
                    <Label>Demo OTP mode</Label>
                  </div>
                  <FormInput
                    label="Demo OTP code"
                    value={draft.auth?.demoOtpCode ?? ""}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        auth: { ...(draft.auth ?? defaultAuth()), demoOtpCode: e.target.value },
                      })
                    }
                  />
                  <div className="grid gap-4 sm:grid-cols-2">
                    <FormInput
                      label="OTP expiry (minutes)"
                      type="number"
                      value={String(draft.auth?.otpExpiryMinutes ?? 10)}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          auth: {
                            ...(draft.auth ?? defaultAuth()),
                            otpExpiryMinutes: Number(e.target.value) || 10,
                          },
                        })
                      }
                    />
                    <FormInput
                      label="OTP length"
                      type="number"
                      value={String(draft.auth?.otpLength ?? 6)}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          auth: {
                            ...(draft.auth ?? defaultAuth()),
                            otpLength: Number(e.target.value) || 6,
                          },
                        })
                      }
                    />
                  </div>
                </>
              )}

              {activeKey === "maps" && (
                <>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={draft.maps.enabled}
                      onCheckedChange={(v) => setDraft({ ...draft, maps: { ...draft.maps, enabled: v } })}
                    />
                    <Label>Enabled</Label>
                  </div>
                  <FormInput
                    label="Provider"
                    value={draft.maps.provider}
                    onChange={(e) => setDraft({ ...draft, maps: { ...draft.maps, provider: e.target.value } })}
                  />
                  <FormInput
                    label={draft.maps.apiKeyConfigured ? "API Key (leave blank to keep)" : "API Key"}
                    type="password"
                    value={draft.maps.apiKey ?? ""}
                    onChange={(e) => setDraft({ ...draft, maps: { ...draft.maps, apiKey: e.target.value } })}
                  />
                </>
              )}
            </div>
          )}

          <SheetFooter className="mt-auto border-t bg-background px-6 py-4 sm:flex-row sm:justify-end sm:gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleDialogSave} disabled={updateMutation.isPending || !activeKey}>
              {dialogMode === "create" ? "Add" : "Save"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <AlertDialog open={Boolean(deleteKey)} onOpenChange={(open) => !open && setDeleteKey(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Remove {PROVIDERS.find((p) => p.key === deleteKey)?.title ?? "provider"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This clears credentials and disables the provider. You can add it again later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default IntegrationsSection;
